import React from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import { Users, MessageSquare, Target, UserCheck } from "lucide-react";

const AgencyDashboard: React.FC = () => {
  const { stats, loading, error } = useAgencyData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar dados</h3>
          <p className="text-neutral-300 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total de Contas",
      value: stats?.totalAccounts || 0,
      icon: Users,
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "Contas Ativas",
      value: stats?.activeAccounts || 0,
      icon: UserCheck,
      color: "text-green-400",
      bgColor: "bg-green-500/10",
    },
    {
      title: "Total de Contatos",
      value: stats?.totalContacts || 0,
      icon: Users,
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
    },
    {
      title: "Total de Mensagens",
      value: stats?.totalMessages || 0,
      icon: MessageSquare,
      color: "text-yellow-400",
      bgColor: "bg-yellow-500/10",
    },
    {
      title: "Total de Campanhas",
      value: stats?.totalCampaigns || 0,
      icon: Target,
      color: "text-red-400",
      bgColor: "bg-red-500/10",
    },
  ];

  return (
    <div className="w-full">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-neutral-50 mb-2">
          Resumo da Agência
        </h3>
        <p className="text-neutral-400 text-sm">
          Visão geral de todas as contas e atividades da agência
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-neutral-800/50 border border-[#3F3F464D] rounded-lg p-6 hover:bg-neutral-800 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-neutral-400 text-sm">{stat.title}</p>
              <p className="text-3xl font-bold text-neutral-50">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-neutral-800/50 border border-[#3F3F464D] rounded-lg p-6">
        <h4 className="text-lg font-semibold text-neutral-50 mb-4">
          Informações
        </h4>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-[#3F3F464D]">
            <span className="text-neutral-400">Média de Contatos por Conta</span>
            <span className="text-neutral-50 font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalContacts / stats.totalAccounts)
                : 0}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#3F3F464D]">
            <span className="text-neutral-400">Média de Mensagens por Conta</span>
            <span className="text-neutral-50 font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalMessages / stats.totalAccounts)
                : 0}
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-neutral-400">Média de Campanhas por Conta</span>
            <span className="text-neutral-50 font-medium">
              {stats?.totalAccounts && stats.totalAccounts > 0
                ? Math.round(stats.totalCampaigns / stats.totalAccounts)
                : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyDashboard;
